import { useEffect, useState } from "react"

import { Database, X } from "@/components/icons"
import { useTranslation } from "react-i18next"

import type { DrillThrough } from "../dashboard/catalog"

import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Spinner } from "@/components/ui/spinner"
import { api } from "@/lib/api-client"

type DrillContext = {
  drill: DrillThrough
  /** The row the user clicked — drives `params_from_row` substitution. */
  row: Record<string, string | number | null>
}

type DrillResponse = {
  columns: string[]
  rows: Array<Record<string, string | number | null>>
  total: number
  truncated: boolean
}

/**
 * Side-sheet that opens when a user clicks a row on a Table with
 * `drillThrough` configured. Runs the detail SQL via a backend
 * endpoint that substitutes the row's column values for `:placeholder`
 * tokens (mirrors how `params_from_state` works for refresh) and
 * renders the result as a simple two-axis table.
 *
 * Closed state: returns null so the sheet only mounts on demand.
 * Auth + connection access are enforced server-side.
 */
export function DrillThroughSheet({
  context,
  onClose,
}: {
  context: DrillContext | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [data, setData] = useState<DrillResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!context) {
      setData(null)
      setError(null)
      return
    }
    let cancelled = false
    setIsLoading(true)
    setError(null)
    setData(null)
    api
      .post<unknown, DrillResponse>("/dashboards/drill-through", {
        sql: context.drill.sql,
        connection_id: context.drill.connection_id,
        params_from_row: context.drill.params_from_row ?? {},
        row: context.row,
      })
      .then((response) => {
        if (cancelled) return
        setData(response)
      })
      .catch((err: Error) => {
        if (cancelled) return
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        setError(err.message ?? "Failed to run drill query")
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [context])

  const open = context !== null

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <SheetContent
        className="flex w-full flex-col gap-3 sm:max-w-2xl"
        data-testid="drill-through-sheet"
        side="right"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-base">
            <Database className="size-4 shrink-0" />
            {context?.drill.title ??
              t("dash.drill.title_fallback", { defaultValue: "Detail view" })}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Spinner className="size-6" />
            </div>
          )}
          {error && !isLoading && (
            <div className="text-destructive rounded-md border px-3 py-2 text-sm">{error}</div>
          )}
          {data && !isLoading && data.rows.length === 0 && (
            <div className="text-muted-foreground px-3 py-8 text-center text-sm">
              {t("dash.drill.no_rows", { defaultValue: "No rows match this selection." })}
            </div>
          )}
          {data && !isLoading && data.rows.length > 0 && (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    {data.columns.map((col) => (
                      <th
                        key={col}
                        className="text-muted-foreground border-b px-3 py-2 text-left text-[11px] font-semibold tracking-wider uppercase"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, i) => (
                    <tr key={i} className="even:bg-muted/20 border-b last:border-0">
                      {data.columns.map((col) => (
                        <td key={col} className="px-3 py-2">
                          {row[col] ?? ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.truncated && (
                <div className="text-muted-foreground bg-muted/30 border-t px-3 py-2 text-xs">
                  {t("dash.drill.truncated", {
                    defaultValue: "Showing first {{shown}} of {{total}} rows.",
                    shown: data.rows.length,
                    total: data.total,
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={onClose}>
            <X className="mr-1 size-3.5" />
            {t("common.close", { defaultValue: "Close" })}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export type { DrillContext }
